/**
 * Field-based blocks (Header / Customer block / Signature) are composed from
 * an ordered list of `BlockField`s. This mirrors the raptor-crm-poc shape so
 * authors can drag rows, pick width (Full / ½), align (L/C/R), and assign a
 * field role (Heading / Label-value / Signature line) without us hard-coding
 * each variant.
 */
export type BlockFieldWidth = 'full' | 'half';
export type BlockFieldAlign = 'left' | 'center' | 'right';

/**
 * The 12-value field-style contract (LLAMS-610 Part 6/7), widened from the 3 legacy values
 * (`heading`, `label_value`, `signature_line`) to match the federated `LayersView` /
 * `fieldRowPrimitives` contract declared by `raptor-crm-fe`. Part 6 landed this as a type-only
 * widening; Part 7 implements normalization and rendering (both renderers) for the other 9.
 *
 * design ruling 2026-09-01 / 2026-08-13 (element catalog): `title` is its own style (not a
 * position-derived heading) so it keeps rendering as the title wherever it is moved; `subheading`
 * names a group inside a section; `value` is exactly one picked dynamic value; `image`/`logo` are
 * media elements with their own settings panel; `rich_text` is a formatted-text field (its `value`
 * holds resolved HTML, unlike every other style here); `dataset` carries placement only — the
 * element whose actual content is bound to the block's own record data (a locked, non-duplicable,
 * movable band). `signature_line` stays in the union for legacy content but is no longer offered
 * by "Add element" — see `ADDABLE_FIELD_STYLES` — a signature line is authored as a `caption` now.
 */
export const BLOCK_FIELD_STYLES = [
  'title',
  'heading',
  'subheading',
  'label_value',
  'value',
  'text',
  'caption',
  'signature_line',
  'image',
  'logo',
  'rich_text',
  'dataset'
] as const;

export type BlockFieldStyle = (typeof BLOCK_FIELD_STYLES)[number];

/** Size step offered by the Image/Logo settings panel's `RwSegmented`. */
export type MediaSize = 'small' | 'medium' | 'large' | 'full';

/** The three real sizes. `'full'` is a legacy alias — see `normalizeMediaSize`. */
export type MediaSizeResolved = 'small' | 'medium' | 'large';

/**
 * `'full'` was the old third size option ("Full Width"); it may still be persisted on existing
 * blocks. Nothing should branch on the raw `MediaSize` union — read it through this normalizer.
 */
export function normalizeMediaSize(size: MediaSize | undefined): MediaSizeResolved {
  if (size === 'full') return 'large';
  if (size === 'small' || size === 'medium' || size === 'large') return size;
  return 'small';
}

/** The sizes an Image element offers: the three boxes, and the full width of its column (banners, footer bands, page-wide pictures). */
export type ImageMediaSize = MediaSizeResolved | 'full';

/**
 * An Image element's size. Unlike a Logo (which only has the three boxes, so `'full'` aliases to `'large'` through
 * `normalizeMediaSize`), an Image keeps `'full'` as its own size.
 */
export function normalizeImageMediaSize(size: MediaSize | undefined): ImageMediaSize {
  return size === 'full' ? 'full' : normalizeMediaSize(size);
}

/** Alignment offered by the same panel's icon-only `RwSegmented`. */
export type MediaAlignment = 'left' | 'center' | 'right';

/**
 * Image / Logo settings. One shape for both styles rather than a discriminated union keyed on
 * `field.style` — `FieldListEditor`'s row → patch plumbing is style-agnostic (it patches whatever
 * `BlockField` it is given), so a union would force every caller through a narrowing step nothing
 * else here needs. `altText` / `description` / `uploadedFile` only apply to `'image'`;
 * `selectedLogoVariant` only to `'logo'` — each panel reads only the part that applies to it.
 */
export interface BlockFieldMedia {
  size: MediaSize;
  alignment: MediaAlignment;
  /** Image only — alt text for screen readers / a broken image. */
  altText?: string;
  /** Image only — a caption printed under the image; distinct from `altText`. */
  description?: string;
  /**
   * Image only. There is no persisted asset-upload contract in this repo yet (LLAMS-610 Part 7
   * caveat, same accepted gap as the branding/background-image work): a URL/asset reference is
   * the only supported source. Kept optional for a future host upload contract.
   */
  imageUrl?: string;
  /** Logo only — which host "Brand Kit" logo variant is selected, when the host offers more than one. */
  selectedLogoVariant?: string;
}

/** ↔ horizontal / ↕ vertical — a label_value row's own orientation control. */
export type BlockFieldOrientation = 'horizontal' | 'vertical';

export interface BlockField {
  id: string;
  label: string;
  /** Free-form text; supports {{quote.token}} placeholders that resolve at render time. */
  value: string;
  width: BlockFieldWidth;
  align: BlockFieldAlign;
  style: BlockFieldStyle;
  visible: boolean;
  sortOrder: number;
  /** Independent label/value visibility (Part 6 contract). `undefined` = true (both shown). */
  labelVisible?: boolean;
  valueVisible?: boolean;
  /**
   * Optional element name, distinct from `label` (Part 6 contract) — the panel's own layers-view
   * row name. Absent = the element's type label ("Label + value", "Heading") — see
   * `panelRowName.ts` for the full naming precedence. Never printed on the document.
   */
  name?: string;
  /** ↔/↕ on label_value rows. Absent = horizontal. */
  orientation?: BlockFieldOrientation;
  /**
   * Which column slot (0-based) this field sits in within its section's layout. Absent = derived
   * by `headerSections.ts`'s legacy normalization.
   */
  column?: number;
  /**
   * Which `HeaderSection` this field belongs to. Absent on pre-section content —
   * `normalizeHeaderSections` assigns one deterministically.
   */
  sectionId?: string;
  /** Image/Logo settings-panel state — meaningless on every other style. */
  media?: BlockFieldMedia;
  /**
   * The Style panel's switches (Figma `be.panel.style`, LLAMS-610 audit): print this element's text in
   * italics / in capitals. Additive and optional - absent or `false` leaves the element's own look
   * (a Heading stays in capitals by its style); read by BOTH renderers (`FieldElementRender`,
   * `FieldElementPdf`). Meaningless on Image / Logo / Dataset.
   */
  italic?: boolean;
  uppercase?: boolean;
}
